/**
 * Motion presets (§13.2 budget table). Every preset is a pair: FULL choreography and a CALM variant that is an
 * instant state change carrying identical text/DOM. Components pick with `pickPreset(preset, energy)`.
 *
 * Durations are seconds (motion's unit). Budgets: control 80–120ms, transition 180–240ms, wheel 2.5–4s, confetti ≤3s.
 */
import type { Transition, Variants } from 'motion/react';
import { MOTION } from '../tokens';
import type { EnergyLevel } from './MotionProvider';

export interface Preset {
  variants: Variants;
  transition: Transition;
  initial: string;
  animate: string;
  exit?: string;
}

export interface PresetPair {
  FULL: Preset;
  CALM: Preset;
}

const instant: Transition = { duration: 0 };
const ms = (n: number) => n / 1000;

export const EASE_STANDARD = [0.2, 0, 0, 1] as const;
export const EASE_OUT = [0.16, 1, 0.3, 1] as const;

/** Control response: opacity/colour or small transform acknowledgement (80–120ms). */
export const controlResponse: PresetPair = {
  FULL: {
    variants: { idle: { scale: 1 }, pressed: { scale: 0.98 }, hover: { scale: 1.01 } },
    transition: { duration: ms(MOTION.control), ease: EASE_STANDARD },
    initial: 'idle',
    animate: 'idle',
  },
  CALM: {
    variants: { idle: { scale: 1 }, pressed: { scale: 1 }, hover: { scale: 1 } },
    transition: instant,
    initial: 'idle',
    animate: 'idle',
  },
};

/** Screen transition: shared prize element (`layoutId`) or short crossfade (180–240ms); calm = instant replace. */
export const screenTransition: PresetPair = {
  FULL: {
    variants: { enter: { opacity: 0, y: 8 }, in: { opacity: 1, y: 0 }, out: { opacity: 0, y: -8 } },
    transition: { duration: ms(MOTION.transition), ease: EASE_STANDARD },
    initial: 'enter',
    animate: 'in',
    exit: 'out',
  },
  CALM: {
    variants: { enter: { opacity: 1, y: 0 }, in: { opacity: 1, y: 0 }, out: { opacity: 1, y: 0 } },
    transition: instant,
    initial: 'enter',
    animate: 'in',
    exit: 'out',
  },
};

/** Layout transition used with `layoutId` for the win-card → product-page shared element (§6.3). */
export const sharedElementTransition: Record<EnergyLevel, Transition> = {
  FULL: { layout: { duration: ms(MOTION.transitionMax), ease: EASE_STANDARD } },
  CALM: { layout: { duration: 0 } },
};

/** Win entrance: prize hero scales in, facts rise after (≤ transition budget per step, no flashing). */
export const winEntrance: PresetPair = {
  FULL: {
    variants: {
      hidden: { opacity: 0, scale: 0.92, y: 16 },
      shown: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.36, ease: EASE_OUT } },
    },
    transition: { duration: 0.36, ease: EASE_OUT },
    initial: 'hidden',
    animate: 'shown',
  },
  CALM: {
    variants: { hidden: { opacity: 1, scale: 1, y: 0 }, shown: { opacity: 1, scale: 1, y: 0 } },
    transition: instant,
    initial: 'hidden',
    animate: 'shown',
  },
};

/** Staggered children for win facts; calm = no stagger. */
export const winFacts: PresetPair = {
  FULL: {
    variants: {
      hidden: {},
      shown: { transition: { staggerChildren: 0.06, delayChildren: 0.12 } },
    },
    transition: { duration: ms(MOTION.transition) },
    initial: 'hidden',
    animate: 'shown',
  },
  CALM: { variants: { hidden: {}, shown: {} }, transition: instant, initial: 'hidden', animate: 'shown' },
};
export const winFactItem: PresetPair = {
  FULL: {
    variants: { hidden: { opacity: 0, y: 6 }, shown: { opacity: 1, y: 0 } },
    transition: { duration: ms(MOTION.transition), ease: EASE_STANDARD },
    initial: 'hidden',
    animate: 'shown',
  },
  CALM: {
    variants: { hidden: { opacity: 1, y: 0 }, shown: { opacity: 1, y: 0 } },
    transition: instant,
    initial: 'hidden',
    animate: 'shown',
  },
};

/** Loss micro-animation: a single soft settle (no shake, no near-miss theatrics). */
export const lossSettle: PresetPair = {
  FULL: {
    variants: { hidden: { opacity: 0, y: 10 }, shown: { opacity: 1, y: 0 } },
    transition: { duration: 0.32, ease: EASE_OUT },
    initial: 'hidden',
    animate: 'shown',
  },
  CALM: {
    variants: { hidden: { opacity: 1, y: 0 }, shown: { opacity: 1, y: 0 } },
    transition: instant,
    initial: 'hidden',
    animate: 'shown',
  },
};

/** Tier reveal: each winner row slides in; operator paces the sequence, not the preset. */
export const tierRevealRow: PresetPair = {
  FULL: {
    variants: { hidden: { opacity: 0, x: -12 }, shown: { opacity: 1, x: 0 } },
    transition: { duration: ms(MOTION.transitionMax), ease: EASE_OUT },
    initial: 'hidden',
    animate: 'shown',
  },
  CALM: {
    variants: { hidden: { opacity: 1, x: 0 }, shown: { opacity: 1, x: 0 } },
    transition: instant,
    initial: 'hidden',
    animate: 'shown',
  },
};

/** Subtle highlight used by calm-mode reveals ("Static result with a subtle highlight"). */
export const calmHighlight: Preset = {
  variants: { off: { outlineColor: 'transparent' }, on: { outlineColor: 'var(--wd-prize-accent)' } },
  transition: instant,
  initial: 'off',
  animate: 'on',
};

export const presets = {
  controlResponse,
  screenTransition,
  winEntrance,
  winFacts,
  winFactItem,
  lossSettle,
  tierRevealRow,
} as const;

export type PresetName = keyof typeof presets;

export function pickPreset(pair: PresetPair, energy: EnergyLevel): Preset {
  return pair[energy];
}

/**
 * Wheel physics timing (§13.2 2.5–4.0s): acceleration → friction easing → tick slowdown.
 * `easeOutWheel(t)` is a quintic ease-out that models friction; the tick cadence slows as the derivative shrinks.
 */
export function easeOutWheel(t: number): number {
  const c = Math.min(1, Math.max(0, t));
  return 1 - Math.pow(1 - c, 5);
}

export function easeInAccel(t: number): number {
  const c = Math.min(1, Math.max(0, t));
  return c * c;
}

export function clampWheelDuration(ms: number): number {
  return Math.min(MOTION.wheelMax, Math.max(MOTION.wheelMin, ms));
}
